import {
  Body1,
  Button,
  Caption1,
  Dropdown,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Text,
  Title3,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  isAmountReportId,
  payrollPeriodFor,
  todayInZone,
  type AvailableReport,
  type ReportId,
} from '@worklogix/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

import { SHRINKABLE } from '../../responsive';
import { useTimeAdminApi } from '../api';
import { DataTable, type DataColumn } from '../components/DataTable';
import { useIsAdmin } from '../useIsAdmin';

import { AmountReportTable } from './AmountReportTable';
import { CommissionReportTable } from './CommissionReportTable';
import { DriverLogTable } from './DriverLogTable';
import { HolidayReportTable } from './HolidayReportTable';
import { PtoReportTable } from './PtoReportTable';

/**
 * A row of report data. No report has a data endpoint yet — each is built one at
 * a time — so the table renders its columns with an empty body. The shape is
 * deliberately generic across both entry shapes: `when` is a date for `hours`
 * reports and a pay period for `amount` ones, and `value` is pre-formatted by
 * whichever loader eventually supplies it.
 */
export interface ReportRow {
  id: string;
  employee: string;
  department: string;
  when: string;
  value: string;
  status: string;
}

/** How long the body cross-fades when switching reports. */
const MORPH_MS = 160;

const useStyles = makeStyles({
  /** The cross-report admin action (#10), above the report picker. */
  bulkBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    columnGap: '8px',
    rowGap: '8px',
    marginBottom: '12px',
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
    paddingTop: tokens.spacingVerticalL,
    // Every ancestor between the page and a table's scroller must be able to
    // shrink, or the overflow lands on the document instead. See responsive.ts.
    ...SHRINKABLE,
  },
  picker: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
    maxWidth: '360px',
  },
  description: {
    color: tokens.colorNeutralForeground3,
  },
  // The table body cross-fades on switch while the picker and surrounding chrome
  // stay put, so changing report reads as one table reshaping rather than a page
  // swap. Motion is suppressed for users who ask for reduced motion.
  surface: {
    transitionProperty: 'opacity, transform',
    transitionDuration: `${MORPH_MS}ms`,
    transitionTimingFunction: tokens.curveEasyEase,
    opacity: 1,
    transform: 'translateY(0)',
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '1ms',
    },
  },
  surfaceFading: {
    opacity: 0,
    transform: 'translateY(4px)',
  },
  state: {
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
  },
});

/** Columns for the selected report, keyed off its entry shape. */
function columnsFor(report: AvailableReport): ReadonlyArray<DataColumn<ReportRow>> {
  const isHours = report.shape === 'hours';
  return [
    { columnId: 'employee', header: 'Employee', renderCell: (r) => r.employee },
    { columnId: 'department', header: 'Department', renderCell: (r) => r.department },
    { columnId: 'when', header: isHours ? 'Date' : 'Pay period', renderCell: (r) => r.when },
    { columnId: 'value', header: isHours ? 'Hours' : 'Amount', renderCell: (r) => r.value },
    { columnId: 'status', header: 'Status', renderCell: (r) => r.status },
  ];
}

/**
 * The "My Reports" surface: one picker listing the reports the signed-in user
 * may see, and a table that morphs into the chosen report.
 *
 * Access is decided ENTIRELY server-side — this component renders whatever
 * `GET /reports/available` returns and never inspects roles. That is the seam:
 * moving from the current role rule to per-user grants is a backend change only.
 * (The dropdown is presentation, not a security boundary; each report's data
 * endpoint enforces access in its own right as it is built.)
 */
export function MyReportsTab(): JSX.Element {
  const styles = useStyles();
  const api = useTimeAdminApi();
  const isAdmin = useIsAdmin();

  const [reports, setReports] = useState<readonly AvailableReport[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<ReportId | null>(null);
  /** The report the table is CURRENTLY showing — lags `selectedId` by one fade. */
  const [rendered, setRendered] = useState<AvailableReport | null>(null);
  const [fading, setFading] = useState(false);

  /** The one admin action that spans every amount report (#10). */
  const [busy, setBusy] = useState<'post' | null>(null);
  const [actionNote, setActionNote] = useState<string | null>(null);
  const [actionIntent, setActionIntent] = useState<'success' | 'warning' | 'error'>('success');

  const period = payrollPeriodFor(todayInZone(new Date())).endDate;

  const runPostAll = useCallback(async (): Promise<void> => {
    setBusy('post');
    setActionNote(null);
    try {
      const res = await api.postAllAmounts(period);
      // Report the legs that had something to say. "Nothing to post" is normal and is
      // stated plainly rather than dressed as a failure.
      const parts = res.legs.map((l) =>
        l.problem !== undefined
          ? `${l.reportId}: ${l.problem}`
          : `${l.reportId}: ${String(l.posted)} posted${l.failed > 0 ? `, ${String(l.failed)} failed` : ''}`
      );
      setActionIntent(res.totalFailed > 0 ? 'warning' : 'success');
      setActionNote(`${String(res.totalPosted)} posted for ${period}. ${parts.join(' · ')}`);
    } catch (err) {
      setActionIntent('error');
      setActionNote(err instanceof Error ? err.message : 'Could not post.');
    } finally {
      setBusy(null);
    }
  }, [api, period]);

  /*
   * "Pull allowances from NetSuite" USED TO BE A BUTTON HERE, and it is gone
   * (Michael, 2026-09-30: "We need to remove the pull allowances button because
   * allowances automatically pull in now").
   *
   * 🔥 A MANUAL-ONLY SWEEP IS HOW A WEEK SILENTLY MISSES. Nothing ran it on a
   * schedule, so the standing allowances landed in the reimbursement report only if
   * somebody remembered to press this — and a week where nobody did looked exactly
   * like a week with no allowances in it. The scheduled `allowance-sweep` job now
   * does it (api/src/jobs/allowance-sweep), which makes the button not merely
   * redundant but misleading: a control that re-does what already happened invites
   * somebody to press it to "make sure", and to read "0 added, 30 already there"
   * as a failure.
   *
   * The endpoint itself is untouched — the job calls the same service.
   */
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .listAvailableReports()
      .then((list) => {
        if (cancelled) {
          return;
        }
        setReports(list);
        const first = list[0];
        if (first !== undefined) {
          setSelectedId(first.id);
          setRendered(first);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : 'Could not load reports.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  // Clear a pending morph timer if the tab unmounts mid-transition.
  useEffect(
    () => () => {
      if (timer.current !== null) {
        clearTimeout(timer.current);
      }
    },
    []
  );

  const selectReport = useCallback(
    (id: ReportId) => {
      if (id === selectedId || reports === null) {
        return;
      }
      const next = reports.find((r) => r.id === id);
      if (next === undefined) {
        return;
      }
      setSelectedId(id);
      setFading(true);
      if (timer.current !== null) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(() => {
        setRendered(next);
        setFading(false);
        timer.current = null;
      }, MORPH_MS);
    },
    [reports, selectedId]
  );

  if (loadError !== null) {
    return (
      <div className={styles.state} role="alert">
        <Text className={styles.error}>{loadError}</Text>
      </div>
    );
  }

  if (reports === null) {
    return (
      <div className={styles.state} role="status">
        <Spinner size="tiny" label="Loading reports…" />
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className={styles.state} role="status">
        <Title3>My Reports</Title3>
        <Body1>You don&apos;t have access to any report items.</Body1>
      </div>
    );
  }

  const selectedLabel = reports.find((r) => r.id === selectedId)?.label ?? '';

  return (
    <div className={styles.root}>
      <div className={styles.picker}>
        <label htmlFor="report-picker">
          <Caption1>Report</Caption1>
        </label>
        <Dropdown
          id="report-picker"
          aria-label="Select a report"
          value={selectedLabel}
          selectedOptions={selectedId === null ? [] : [selectedId]}
          onOptionSelect={(_, data) => {
            if (data.optionValue !== undefined) {
              selectReport(data.optionValue as ReportId);
            }
          }}
        >
          {reports.map((report) => (
            <Option key={report.id} value={report.id} text={report.label}>
              {report.label}
            </Option>
          ))}
        </Dropdown>
        {rendered !== null ? (
          <Caption1 className={styles.description}>{rendered.description}</Caption1>
        ) : null}
      </div>

      {/*
        * ONE BUTTON FOR EVERYTHING THAT IS NOT A TIME BILL (Michael, 2026-09-17, #10):
        * "I want one button that when pressed posts everything that falls in this
        * category to the separate paychecks on the payroll batch."
        *
        * It lives ABOVE the report picker on purpose — it spans bonuses,
        * reimbursements and commission, so putting it inside any one report would
        * imply it only acts on that one. Admin only, because it writes pay.
        */}
      {isAdmin ? (
        <div className={styles.bulkBar}>
          <Button
            appearance="primary"
            disabled={busy !== null}
            onClick={() => void runPostAll()}
          >
            {busy === 'post' ? <Spinner size="tiny" /> : 'Post all to NetSuite'}
          </Button>
          {actionNote !== null ? (
            <MessageBar intent={actionIntent}>
              <MessageBarBody>{actionNote}</MessageBarBody>
            </MessageBar>
          ) : null}
        </div>
      ) : null}

      <div
        className={mergeClasses(styles.surface, fading ? styles.surfaceFading : undefined)}
        aria-busy={fading}
      >
        {rendered === null ? null : rendered.id === 'pto' ? (
          <PtoReportTable />
        ) : rendered.id === 'holiday' ? (
          <HolidayReportTable />
        ) : rendered.id === 'commission' ? (
          // Commission is READ-ONLY and comes from NetSuite (Michael, 2026-09-11),
          // so it no longer shares the editable amount table with bonus and
          // reimbursement — there is nothing to enter.
          <CommissionReportTable />
        ) : isAmountReportId(rendered.id) ? (
          // Bonus / Reimbursements — one component for both, keyed by report so
          // switching remounts rather than showing the previous report's rows
          // against the new heading.
          <AmountReportTable key={rendered.id} reportId={rendered.id} />
        ) : rendered.id === 'driverLog' ? (
          <DriverLogTable />
        ) : (
          // Nothing left unbuilt today; this branch keeps the shape-only fallback so a
          // report added to the catalogue renders its columns rather than nothing.
          <DataTable<ReportRow>
            columns={columnsFor(rendered)}
            rows={[]}
            getRowId={(r) => r.id}
            showHeaderWhenEmpty
            emptyMessage={`No ${rendered.label.toLowerCase()} entries yet.`}
          />
        )}
      </div>
    </div>
  );
}
